import { useMemo, useState } from 'react';
import { Clipboard, Download } from 'lucide-react';
import type { AnalysisResult } from '../domain/models';
import { visitsToCsv, segmentsToCsv, dailySummariesToCsv } from '../export/csv';
import { analysisToGeoJson } from '../export/geojson';
import { analysisToMarkdown } from '../export/markdown';

type ExportKind = 'visits' | 'segments' | 'daily' | 'geojson' | 'markdown';

const labels: Record<ExportKind, string> = {
  visits: 'CSV visits',
  segments: 'CSV segments',
  daily: 'CSV daily summaries',
  geojson: 'GeoJSON',
  markdown: 'Markdown',
};

export function ExportPanel({ result }: { result: AnalysisResult }) {
  const [kind, setKind] = useState<ExportKind>('markdown');
  const content = useMemo(() => renderExport(kind, result), [kind, result]);

  const fileName = `${result.fileName.replace(/\.json$/i, '') || 'timeline'}_${kind}.${kind === 'geojson' ? 'geojson' : kind === 'markdown' ? 'md' : 'csv'}`;

  const download = () => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="section">
      <div className="button-row">
        <div className="field">
          <label htmlFor="export-kind">出力</label>
          <select id="export-kind" value={kind} onChange={(event) => setKind(event.target.value as ExportKind)}>
            {Object.entries(labels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <button className="button primary" type="button" onClick={download}>
          <Download size={16} aria-hidden="true" />
          ダウンロード
        </button>
        <button className="button" type="button" onClick={() => navigator.clipboard.writeText(content)}>
          <Clipboard size={16} aria-hidden="true" />
          コピー
        </button>
      </div>
      <textarea className="textarea" readOnly value={content} aria-label="Export preview" />
    </div>
  );
}

function renderExport(kind: ExportKind, result: AnalysisResult): string {
  switch (kind) {
    case 'visits':
      return visitsToCsv(result.visits);
    case 'segments':
      return segmentsToCsv(result.segments);
    case 'daily':
      return dailySummariesToCsv(result.dailySummaries);
    case 'geojson':
      return analysisToGeoJson(result);
    case 'markdown':
      return analysisToMarkdown(result);
  }
}
